import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle, Copy, CreditCard, ExternalLink, Loader2, QrCode } from 'lucide-react';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { toast } from '@/shared/components/Toast';
import { formatCurrency } from '@/shared/lib/money';
import { applyCepMask, applyCnpjMask, applyCpfMask, applyPhoneMask } from '@/shared/lib/masks';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { copyToClipboard } from '../../../lib/clipboard';
import { createClientId } from '../../../lib/random-id';
import { revalidateAiUnits, useAiUnitPurchase, useCreateAiUnitPurchase, usePricing } from '../hooks/useBilling';
import type { AiUnitPack, AiUnitPaymentMethod, AiUnitPurchase, PersonType } from '../types/billing.types';
import {
  AI_UNITS_REFUND_TEXT,
  formatUnits,
  getAiUnitPurchaseFailure,
  type AiUnitPurchaseFailure,
} from '../lib/ai-units';
import { billingInfoSchema, toBillingInfo, type BillingInfoFormData } from '../lib/billing-info';
import styles from './AiUnitsPurchaseDialog.module.css';

interface AiUnitsPurchaseDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Comprar atendimentos" (change billing-ai-units). Lista os pacotes à venda
 * (`aiUnitPacks[]`), oferece Pix (padrão) e cartão, sem boleto, e pede os
 * dados de cobrança só quando a API os exige. Por Pix, o QR se desenha do
 * copia-e-cola; por cartão, o link abre em outra aba. A confirmação chega por
 * `billing.ai_units_updated` (que relê a compra) e, enquanto o diálogo está
 * aberto, pela releitura a cada 5 s.
 *
 * A `Idempotency-Key` nasce com a primeira tentativa de cada abertura e fica
 * a mesma enquanto a escolha não muda: o clique duplo e a nova tentativa
 * depois de uma falha do gateway retomam a mesma compra, sem segunda
 * cobrança. Outra escolha na mesma abertura é outra compra, com outra chave
 * (a mesma chave com outro corpo é recusada pela API).
 *
 * O conteúdo só existe aberto, para cada abertura nascer limpa.
 */
export function AiUnitsPurchaseDialog(props: AiUnitsPurchaseDialogProps) {
  if (!props.open) return null;
  return <AiUnitsPurchaseDialogContent {...props} />;
}

const PAYMENT_METHODS: { value: AiUnitPaymentMethod; label: string; icon: typeof QrCode }[] = [
  { value: 'pix', label: 'Pix', icon: QrCode },
  { value: 'credit_card', label: 'Cartão', icon: CreditCard },
];

/** "12 meses a partir do pagamento". */
function validityText(months: number): string {
  return `${months} ${months === 1 ? 'mês' : 'meses'} a partir do pagamento`;
}

/** "R$ 0,33 por atendimento", com até quatro casas quando o preço pede. */
function unitPriceText(unitPrice: number): string {
  const text = unitPrice.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return `${text} por atendimento`;
}

function AiUnitsPurchaseDialogContent({ onClose }: AiUnitsPurchaseDialogProps) {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const user = useAuthStore((s) => s.user);
  const { data: pricing, isLoading: pricingLoading, isError: pricingError, refetch: refetchPricing } = usePricing();
  const packs = pricing?.aiUnitPacks ?? [];

  const [packCode, setPackCode] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<AiUnitPaymentMethod>('pix');
  const [personType, setPersonType] = useState<PersonType>('pf');
  const [needsBillingInfo, setNeedsBillingInfo] = useState(false);
  const [failure, setFailure] = useState<AiUnitPurchaseFailure | null>(null);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  // A chave da tentativa: a primeira de cada abertura, trocada só quando a escolha muda.
  const attempt = useRef<{ hash: string; key: string } | null>(null);

  const mutation = useCreateAiUnitPurchase();
  const { data: purchase } = useAiUnitPurchase(purchaseId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BillingInfoFormData>({
    resolver: zodResolver(billingInfoSchema),
    defaultValues: { name: user?.name ?? '', email: user?.email ?? '' },
  });

  // Pago (pelo evento ou pela releitura): o card e as faturas leem de novo.
  const status = purchase?.status;
  useEffect(() => {
    if (status === 'paid') revalidateAiUnits(queryClient, tenantId);
  }, [status, queryClient, tenantId]);

  const selectedPack = packs.find((pack) => pack.code === packCode) ?? null;

  const close = () => {
    if (mutation.isPending) return;
    onClose();
  };

  async function buy(billingInfo?: BillingInfoFormData) {
    if (!selectedPack) return;
    const hash = `${selectedPack.code}|${paymentMethod}`;
    if (!attempt.current || attempt.current.hash !== hash) {
      attempt.current = { hash, key: createClientId() };
    }

    setFailure(null);
    try {
      const result = await mutation.mutateAsync({
        packCode: selectedPack.code,
        paymentMethod,
        billingInfo: billingInfo ? toBillingInfo(personType, billingInfo) : undefined,
        idempotencyKey: attempt.current.key,
      });
      setPurchaseId(result.purchase.purchaseId);
    } catch (caught) {
      const next = getAiUnitPurchaseFailure(caught);
      if (next.kind === 'key_reused') attempt.current = null;
      if (next.kind === 'billing_info') setNeedsBillingInfo(true);
      if (next.kind === 'pack_not_found') {
        setPackCode(null);
        void refetchPricing();
      }
      setFailure(next);
    }
  }

  // Com os dados de cobrança pedidos, eles passam pela validação do formulário antes do pedido.
  function submit() {
    if (needsBillingInfo) {
      void handleSubmit((data) => buy(data))();
      return;
    }
    void buy();
  }

  /* ---- Depois da compra criada ---- */
  if (purchase) {
    return (
      <Modal
        open
        onClose={close}
        title={purchase.status === 'paid' ? 'Atendimentos liberados' : 'Pague o pacote'}
        size="sm"
        footer={
          <Button variant={purchase.status === 'pending' ? 'secondary' : 'primary'} onClick={close}>
            {purchase.status === 'pending' ? 'Pagar depois' : 'Fechar'}
          </Button>
        }
      >
        <PurchaseState purchase={purchase} />
      </Modal>
    );
  }

  /* ---- Escolha do pacote e da forma de pagamento ---- */
  const retrying = failure?.kind === 'gateway' || failure?.kind === 'network';
  return (
    <Modal
      open
      onClose={close}
      title="Comprar atendimentos"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={mutation.isPending}
            disabled={!selectedPack || failure?.kind === 'plan_required'}
          >
            {retrying ? 'Tentar de novo' : 'Comprar'}
          </Button>
        </div>
      }
    >
      <p className={styles.sectionLabel} id="ai-units-pack-label">Pacote</p>
      {pricingLoading && <p className={styles.muted} role="status">Carregando pacotes…</p>}
      {pricingError && (
        <p className={styles.error} role="alert">
          Não foi possível carregar os pacotes.{' '}
          <button type="button" className={styles.linkButton} onClick={() => void refetchPricing()}>
            Tentar de novo
          </button>
        </p>
      )}
      {!pricingLoading && !pricingError && packs.length === 0 && (
        <p className={styles.muted}>Nenhum pacote à venda no momento.</p>
      )}
      <div className={styles.packGroup} role="radiogroup" aria-labelledby="ai-units-pack-label">
        {packs.map((pack) => (
          <PackOption
            key={pack.code}
            pack={pack}
            selected={pack.code === packCode}
            disabled={mutation.isPending}
            onSelect={() => {
              setPackCode(pack.code);
              setFailure(null);
            }}
          />
        ))}
      </div>

      <p className={styles.sectionLabel} id="ai-units-payment-label">Forma de pagamento</p>
      <div className={styles.paymentGroup} role="radiogroup" aria-labelledby="ai-units-payment-label">
        {PAYMENT_METHODS.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={paymentMethod === value}
            disabled={mutation.isPending}
            className={`${styles.paymentOption} ${paymentMethod === value ? styles.selected : ''}`}
            onClick={() => {
              setPaymentMethod(value);
              setFailure(null);
            }}
          >
            <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      <div className={styles.terms}>
        <p className={styles.termsText}>
          {selectedPack
            ? `Os atendimentos valem ${validityText(selectedPack.validityMonths)}.`
            : 'Os atendimentos de cada pacote valem a partir do pagamento, pelo prazo de validade dele.'}
        </p>
        <p className={styles.termsText}>{AI_UNITS_REFUND_TEXT}</p>
      </div>

      {needsBillingInfo && (
        <div className={styles.billingInfo}>
          <p className={styles.sectionLabel}>Dados de cobrança</p>
          <p className={styles.muted}>A loja ainda não tem cadastro de cobrança. Preencha uma vez para continuar.</p>
          <div className={styles.personTypeRow} role="radiogroup" aria-label="Tipo de pessoa">
            {(['pf', 'pj'] as const).map((type) => (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={personType === type}
                className={`${styles.paymentOption} ${personType === type ? styles.selected : ''}`}
                onClick={() => setPersonType(type)}
              >
                {type === 'pf' ? 'Pessoa física' : 'Pessoa jurídica'}
              </button>
            ))}
          </div>
          <div className={styles.fieldGroup}>
            <Input label="Nome completo" required {...register('name')} error={errors.name?.message} />
            <Input label="E-mail" required type="email" {...register('email')} error={errors.email?.message} />
            <Input
              label="Telefone"
              required
              placeholder="(11) 99999-8888"
              {...register('phone', {
                onChange: (e) => {
                  e.target.value = applyPhoneMask(e.target.value);
                  setValue('phone', e.target.value, { shouldValidate: false });
                },
              })}
              error={errors.phone?.message}
            />
            <Input
              label={personType === 'pf' ? 'CPF' : 'CNPJ'}
              required
              {...register('document', {
                onChange: (e) => {
                  const mask = personType === 'pf' ? applyCpfMask : applyCnpjMask;
                  e.target.value = mask(e.target.value);
                  setValue('document', e.target.value, { shouldValidate: false });
                },
              })}
              error={errors.document?.message}
            />
            <Input
              label="CEP"
              required
              placeholder="00000-000"
              {...register('zipCode', {
                onChange: (e) => {
                  e.target.value = applyCepMask(e.target.value);
                  setValue('zipCode', e.target.value, { shouldValidate: false });
                },
              })}
              error={errors.zipCode?.message}
            />
          </div>
        </div>
      )}

      {failure && (
        <p className={styles.error} role="alert">{failure.message}</p>
      )}
    </Modal>
  );
}

interface PackOptionProps {
  pack: AiUnitPack;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function PackOption({ pack, selected, disabled, onSelect }: PackOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      className={`${styles.packOption} ${selected ? styles.selected : ''}`}
      onClick={onSelect}
    >
      <span className={styles.packUnits}>{formatUnits(pack.units)} atendimentos</span>
      <span className={styles.packPrice}>{formatCurrency(pack.price)}</span>
      <span className={styles.packDetail}>{unitPriceText(pack.unitPrice)}</span>
      <span className={styles.packDetail}>Validade de {pack.validityMonths} {pack.validityMonths === 1 ? 'mês' : 'meses'}</span>
    </button>
  );
}

/** A compra criada: o Pix ou o link do cartão enquanto aguarda, e o desfecho depois. */
function PurchaseState({ purchase }: { purchase: AiUnitPurchase }) {
  const summary = `${formatUnits(purchase.units)} atendimentos: ${formatCurrency(purchase.amount)}`;

  if (purchase.status === 'paid') {
    return (
      <div className={styles.done} role="status">
        <CheckCircle size={40} strokeWidth={1.5} aria-hidden="true" className={styles.doneIcon} />
        <p className={styles.mainText}>Atendimentos liberados</p>
        <p className={styles.text}>
          {`${formatUnits(purchase.units)} atendimentos já estão no seu saldo e valem ${validityText(purchase.validityMonths)}.`}
        </p>
      </div>
    );
  }

  if (purchase.status === 'expired') {
    return (
      <div className={styles.awaiting} role="status">
        <p className={styles.mainText}>{summary}</p>
        <p className={styles.text}>A cobrança venceu sem pagamento. Para comprar, abra a compra de novo.</p>
      </div>
    );
  }

  if (purchase.status === 'refunded' || purchase.status === 'charged_back') {
    return (
      <div className={styles.awaiting} role="status">
        <p className={styles.mainText}>{summary}</p>
        <p className={styles.text}>Esta compra foi estornada.</p>
      </div>
    );
  }

  return <AwaitingPayment purchase={purchase} summary={summary} />;
}

function AwaitingPayment({ purchase, summary }: { purchase: AiUnitPurchase; summary: string }) {
  const pix = purchase.paymentMethod === 'pix' ? purchase.pix : null;

  async function copyPayload() {
    if (!pix) return;
    if (await copyToClipboard(pix.copyPaste)) {
      toast('success', 'Código Pix copiado.');
    } else {
      toast('error', 'Não foi possível copiar. Selecione o código e copie.');
    }
  }

  return (
    <div className={styles.awaiting}>
      <p className={styles.mainText}>{summary}</p>

      {pix ? (
        <div className={styles.pix}>
          <QRCodeSVG
            value={pix.copyPaste}
            size={200}
            marginSize={2}
            title="QR Code do Pix"
            className={styles.qr}
            role="img"
            aria-label="QR Code do Pix"
          />
          <label className={styles.pixLabel} htmlFor="ai-units-pix-payload">Pix copia e cola</label>
          <div className={styles.pixRow}>
            <input
              id="ai-units-pix-payload"
              className={styles.pixPayload}
              readOnly
              value={pix.copyPaste}
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button variant="secondary" size="sm" onClick={copyPayload}>
              <Copy size={16} strokeWidth={1.5} aria-hidden="true" />
              Copiar código
            </Button>
          </div>
        </div>
      ) : (
        purchase.invoiceUrl && (
          <Button variant="primary" onClick={() => window.open(purchase.invoiceUrl!, '_blank', 'noopener')}>
            <ExternalLink size={18} strokeWidth={1.5} aria-hidden="true" />
            {purchase.paymentMethod === 'credit_card' ? 'Pagar com cartão' : 'Abrir a cobrança'}
          </Button>
        )
      )}

      <p className={styles.waiting} role="status">
        <Loader2 size={16} strokeWidth={1.5} aria-hidden="true" className={styles.spin} />
        Aguardando a confirmação do pagamento. Os atendimentos entram no saldo assim que ele chegar.
      </p>
    </div>
  );
}
